import { getDefaultCategory, TreatmentCategory } from 'lib/cdtCodeDefaults'

export type ProviderBucket = `hygiene` | `provider`

export type PreferredProvider = {
  providerId: string // normalized Kolla resource id (matches Provider.vendorId, e.g. `provider_47`)
  lastSeenDate: string // ISO date of that provider's most recent matching procedure
  visitCount: number // distinct days on which that provider performed matching procedures
}

export type TransactionLike = {
  providerId?: string | null
  procedureCode?: string | null
  transactionDate?: string | null
}

export type AppointmentTypeLike = {
  procedureCodes?: (string | null)[] | null
}

/**
 * Provider identity is the Kolla resource id (`resources/provider_47` → remote id
 * `provider_47`, stored as Provider.vendorId). Synced records decorate that id
 * differently: Transaction.providerId uses the internal Provider.id form
 * `K-<locationKey>-provider_47`, Appointment.providerId stores the bare id, and
 * either can be a comma-joined list for multi-provider work. This flattens any of
 * those shapes to bare resource ids.
 */
export function normalizeProviderIds(raw: string | null | undefined): string[] {
  return (raw ?? ``)
    .split(`,`)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) =>
      s
        .split(`/`)
        .pop()!
        .replace(/^K-[^-]+-/, ``),
    )
}

function cleanCodes(codes: (string | null)[] | null | undefined): string[] {
  return (codes ?? []).filter((c): c is string => !!c)
}

export function bucketForProcedureCodes(procedureCodes: string[]): ProviderBucket {
  const categories = procedureCodes.map((code) => getDefaultCategory(code))
  const isHygiene = categories.some((c) => c === TreatmentCategory.PREVENTATIVE || c === TreatmentCategory.PERIODONTICS)
  return isHygiene ? `hygiene` : `provider`
}

export function bucketForAppointmentType(appointmentType: AppointmentTypeLike): ProviderBucket {
  return bucketForProcedureCodes(cleanCodes(appointmentType.procedureCodes))
}

type CodeClass = `hygiene` | `diagnostic` | `substantive`

function classifyCode(code: string): CodeClass {
  const category = getDefaultCategory(code)
  if (category === TreatmentCategory.PREVENTATIVE || category === TreatmentCategory.PERIODONTICS) return `hygiene`
  if (category === TreatmentCategory.DIAGNOSTIC) return `diagnostic`
  return `substantive`
}

type ProcedureRow = { providerId: string; date: string }

function pickPreferred(rows: ProcedureRow[], countRows?: ProcedureRow[]): PreferredProvider | undefined {
  if (!rows.length) return undefined
  const sorted = [...rows].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
  const providerId = sorted[0].providerId
  const scope = (countRows ?? rows).filter((r) => r.providerId === providerId)
  const lastSeenDate = scope.reduce((max, r) => (r.date > max ? r.date : max), sorted[0].date)
  const visitCount = new Set(scope.map((r) => r.date.slice(0, 10))).size
  return { providerId, lastSeenDate, visitCount }
}

/**
 * Derives the preferred dentist and hygienist from Procedure transactions rather
 * than appointments: transactions attribute each procedure to the provider who
 * performed it (a combined cleaning+exam visit is one appointment under one
 * provider but several per-provider transaction rows), and unlike appointment
 * procedureCodes they are populated for the full synced history.
 */
export function computePreferredProvidersFromTransactions(transactions: TransactionLike[]): {
  preferredProvider?: PreferredProvider
  preferredHygienist?: PreferredProvider
} {
  const hygiene: ProcedureRow[] = []
  const substantive: ProcedureRow[] = []
  const diagnostic: ProcedureRow[] = []
  for (const tx of transactions) {
    if (!tx.transactionDate || !tx.procedureCode) continue
    const providerIds = normalizeProviderIds(tx.providerId)
    if (!providerIds.length) continue
    const cls = classifyCode(tx.procedureCode)
    const target = cls === `hygiene` ? hygiene : cls === `diagnostic` ? diagnostic : substantive
    for (const providerId of providerIds) target.push({ providerId, date: tx.transactionDate })
  }
  // Exams and x-rays (Diagnostic) are frequently billed under the hygienist during
  // a cleaning visit, so substantive work decides the dentist; Diagnostic rows only
  // decide when the patient has no substantive history at all.
  const doctorRows = [...substantive, ...diagnostic]
  return {
    preferredProvider: pickPreferred(substantive, doctorRows) ?? pickPreferred(diagnostic),
    preferredHygienist: pickPreferred(hygiene),
  }
}
